import type { ReactNode } from 'react'
import { Check } from './icons'
import { Spinner } from './Spinner'

/** Toggle chip (multi-select). `busy`: the tap is still being saved (a small spinner where the check sits). */
export function ToggleChip({ on, onToggle, children, busy = false }: { on: boolean; onToggle: () => void; children: ReactNode; busy?: boolean }) {
  return (
    <button type="button" aria-pressed={on} aria-busy={busy || undefined} onClick={onToggle}
      className={`gh-chip press inline-flex min-h-11 items-center gap-1.5 px-4 text-[13px] font-semibold leading-[18px] ring-1 ring-inset transition-colors ${
        on ? 'bg-accent text-on-accent ring-accent' : 'bg-transparent text-ink ring-boundary'
      }`}>
      {/* The check's space is reserved on both sides: the label stays centered, and selecting never changes the chip's width. */}
      {busy ? (
        <span aria-hidden="true" className="flex size-4 items-center justify-center"><Spinner size={14} label={false} className="text-current" /></span>
      ) : (
        <span aria-hidden="true" className={on ? 'opacity-100' : 'opacity-0'}><Check size={16} strokeWidth={2.5} /></span>
      )}
      {children}
      <span aria-hidden="true" className="w-4 shrink-0" />
    </button>
  )
}

/** Small static tag. `label` (R10): square corners, so it never reads as a filter chip (those are round pills). */
export function Tag({ children, tone = 'plain', label = false }: { children: ReactNode; tone?: 'plain' | 'accent' | 'surface'; label?: boolean }) {
  const cls = {
    plain: 'bg-sunk text-ink-2',
    accent: 'bg-accent-soft text-accent-ink',
    surface: 'bg-surface/85 text-ink',
  }[tone]
  // Astra quick win: a 24px badge with 12px text, supporting the title instead of competing with it
  return <span className={`inline-flex h-6 shrink-0 cursor-default select-none items-center gap-1 whitespace-nowrap px-2.5 text-[12px] font-semibold leading-none ${label ? 'rounded-[6px]' : 'rounded-full'} ${cls}`}>{children}</span>
}
